import { type JSX, useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { formatDateString } from "common/utils/dates";
import Icon, { ICON_COLORS, Icons } from "../../../Common/Icons";
import { ManualPaginationConfig } from "../../../Common/Tables/hooks";
import Table from "../../../Common/Tables/Table";
import FileDisplay from "../../../Inputs/FileDisplay";
import { TimeInfo } from "../__styles__/JobsTable";
import {
  DocumentTemplate as AccountDocumentTemplate,
  DocumentTemplatesPagination,
} from "./__queries__/documentTemplates";
import { ActionButton } from "./ActionButton";

export type DocumentTemplate = AccountDocumentTemplate & {
  actionButton?: ({}) => JSX.Element;
};

type Props = {
  manualPaginationConfig: ManualPaginationConfig;
  previousDocumentTemplates: DocumentTemplate[];
  documentTemplates: DocumentTemplate[];
  loading: boolean;
  onUpdate: () => void;
  pageInfo?: DocumentTemplatesPagination;
};

export default ({
  previousDocumentTemplates,
  documentTemplates,
  loading,
  onUpdate,
  manualPaginationConfig,
}: Props) => {
  const columns: Array<ColumnDef<DocumentTemplate>> = useMemo(
    () => [
      {
        accessorKey: "task",
        id: "task",
        size: 100,
        cell: ({ row }) => {
          return (
            <FileDisplay titleText={row.original.name}>
              <Icon icon={Icons.CALENDAR} color={ICON_COLORS.GREY_4} />
              <TimeInfo>
                {`Created on ${formatDateString({
                  format: "MM D, YYYY at HH:MM ZZ",
                  dateString: row.original.createdAt.toString(),
                }).toLocaleString()}`}
              </TimeInfo>
            </FileDisplay>
          );
        },
      },
      {
        id: "actions",
        size: 150,
        maxSize: 150,
        cell: ({ row }) => (
          <ActionButton documentTemplate={row.original} onUpdate={onUpdate} />
        ),
        meta: { className: "actions-cell" },
      },
    ],
    [onUpdate],
  );

  return (
    <Table<DocumentTemplate>
      css={{
        "& .actions-cell": {
          textAlign: "right",
        },
      }}
      currentData={documentTemplates}
      previousData={previousDocumentTemplates}
      columns={columns}
      loadingDetails={{
        loading,
        loadingText: "Loading document templates",
        noDataText: "No document templates found",
      }}
      minRows={documentTemplates.length}
      manualPaginationConfig={manualPaginationConfig}
      tableStyleDetails={{
        hasFlushedSides: true,
      }}
      excludeTableHeader
    />
  );
};
